import { MfaMethodEnum, useLoginMutation } from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import {
	Alert,
	Anchor,
	Box,
	Button,
	Group,
	Paper,
	PasswordInput,
	Stack,
	Text,
	TextInput,
	Title,
} from '@mantine/core';
import { IconAlertCircle, IconLock, IconMail } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';
import { handleLoginValidate } from '../utils/login';

export function LoginPage() {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const navigate = useNavigate();

	const colors = useThemeColors();

	const [login, { loading }] = useLoginMutation({
		onError: (err) => {
			setError(err.message || 'Login failed. Please check your credentials.');
		},
	});

	const handleLogin = async () => {
		setError(null);

		try {
			const result = await login({
				variables: {
					input: {
						authKey: email,
						authSecret: password,
					},
				},
			});

			if (result.data?.login) {
				const { token, userId, mfaRequired, mfaMethod, role, supportsBackupCodes } =
					result.data.login;

				// Block non-admin users from accessing the admin portal
				if (role !== 'ADMIN') {
					setError('This portal is for administrators only. Please use the mobile app to access your account.');
					return;
				}

				if (mfaRequired) {
					switch (mfaMethod) {
						case MfaMethodEnum.Email:
							navigate('/auth/mfa/email-verify', {
								state: { userId },
							});
							break;
						case MfaMethodEnum.Authenticator:
							navigate('/auth/mfa/verify', {
								state: {
									userId,
									supportsBackupCodes,
								},
							});
							break;
						default:
							break;
					}
				} else {
					// Redirect to appropriate app based on user role
					handleLoginValidate(token, role);
				}
			}
		} catch {
			// Error handled by mutation onError
		}
	};

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					miw={{ base: 'calc(100vw - 20px)', xs: 480 }}
					maw={480}
					mx={{ sm: 'auto' }}
					p={{ base: 'xs', sm: 'xl' }}
				>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={28} />
							<div>
								<Title mb={4}>Welcome back</Title>
								<Text size="sm" c={colors.textMuted}>
									Sign in to your account
								</Text>
							</div>
						</Stack>

						{error && (
							<Alert
								icon={<IconAlertCircle size={20} color={colors.error} />}
								color="error"
							>
								{error}
							</Alert>
						)}

						<Stack>
							<TextInput
								name="email"
								type="email"
								label="Email"
								placeholder="your@email.com"
								leftSection={<IconMail size={18} />}
								value={email}
								onChange={(e) => setEmail(e.currentTarget.value)}
								disabled={loading}
								required
							/>

							<PasswordInput
								name="password"
								label="Password"
								placeholder="Your password"
								leftSection={<IconLock size={18} />}
								value={password}
								onChange={(e) => setPassword(e.currentTarget.value)}
								disabled={loading}
								required
								onKeyDown={(e) => {
									if (e.key === 'Enter' && email && password) {
										handleLogin();
									}
								}}
							/>
						</Stack>

						<Stack gap="xs">
							<Group justify="flex-end">
								<Anchor component={Link} to="/auth/forgot-password">
									Forgot password?
								</Anchor>
							</Group>
							<Button
								fullWidth
								onClick={handleLogin}
								loading={loading}
								disabled={!email || !password}
							>
								Sign In
							</Button>
						</Stack>

						{/* <Group justify="center">
							<Text size="sm" c={colors.textMuted}>
								Don&apos;t have an account?{' '}
								<Anchor onClick={() => navigate('/signup')} component="span">
									Sign up
								</Anchor>
							</Text>
						</Group> */}
					</Stack>
				</Paper>
			</motion.div>
		</Box>
	);
}
